import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Trophy } from "lucide-react";
import { REFERENCE_TYPES, LIFT_LABELS } from "@/lib/strength";

const MAIN_LIFTS = ["squat", "bench", "deadlift"];

function draftFromMaxes(maxes = []) {
  const values = { squat: "", bench: "", deadlift: "" };
  const refs = { squat: "tm", bench: "tm", deadlift: "tm" };
  maxes.forEach((m) => {
    if (values[m.lift] !== undefined) {
      values[m.lift] = String(m.value_kg);
      refs[m.lift] = m.reference_type || "tm";
    }
  });
  return { values, refs };
}

export default function LiftMaxesEditor({ compact = false, onSaved }) {
  const [draft, setDraft] = useState({ values: { squat: "", bench: "", deadlift: "" }, refs: { squat: "tm", bench: "tm", deadlift: "tm" } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lift-maxes");
      setDraft(draftFromMaxes(data || []));
    } catch {
      /* pas de maxes */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const maxes = MAIN_LIFTS.map((lift) => ({
        lift,
        value_kg: draft.values[lift] || null,
        reference_type: draft.refs[lift] || "tm",
      }));
      const { data } = await api.put("/lift-maxes", { maxes });
      setDraft(draftFromMaxes(data || []));
      toast.success("Références force enregistrées");
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Chargement des max...
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="lift-maxes-editor">
      {!compact && (
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
            <Trophy className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Mes références force (SBD)</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              TM ou 1RM — utilisés pour calculer vos charges cibles dans le programme.
            </p>
          </div>
        </div>
      )}

      <div className={`grid gap-3 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-3"}`}>
        {MAIN_LIFTS.map((lift) => (
          <div key={lift}>
            <Label className="text-xs text-slate-500 capitalize">{LIFT_LABELS[lift] || lift}</Label>
            <div className="flex gap-1.5 mt-1">
              <Input
                type="number"
                step="0.5"
                value={draft.values[lift]}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    values: { ...prev.values, [lift]: e.target.value },
                  }))
                }
                placeholder="kg"
                className="h-9"
              />
              <Select
                value={draft.refs[lift]}
                onValueChange={(v) =>
                  setDraft((prev) => ({
                    ...prev,
                    refs: { ...prev.refs, [lift]: v },
                  }))
                }
              >
                <SelectTrigger className="w-[88px] h-9 text-xs shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REFERENCE_TYPES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.value === "tm" ? "TM" : "1RM"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        size={compact ? "sm" : "default"}
        onClick={save}
        disabled={saving}
        className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Enregistrer les max
      </Button>
    </div>
  );
}